import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import { ShieldAlert, MailCheck, RefreshCw } from "lucide-react";
import { auth } from "../../firebase";
import useCurrentUser from "../../hooks/useCurrentUser";
import api from "../../utils/api";
import { HASBRO_BRAND } from "./hasbroTheme";

export const HASBRO_COMPANY_ID = "hasbro";
export const HASBRO_EMAIL_DOMAIN = "@ap.hasbro.com";

export const isHasbroEmail = email => (email || "").toLowerCase().endsWith(HASBRO_EMAIL_DOMAIN);

// /hasbro workspace: only verified @ap.hasbro.com accounts assigned to the Hasbro company.
// The backend enforces the same rule on every Control Tower call — this guard is UX only.
export default function HasbroGuard({ children }) {
    const { user, clientId, loading } = useCurrentUser();
    const [verified, setVerified] = useState(null);   // set after a reload() of the Firebase user
    const [status, setStatus] = useState("");
    const [busy, setBusy] = useState(false);
    const [codeSent, setCodeSent] = useState(false);
    const [code, setCode] = useState("");

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center text-white/80 text-sm" style={{ background: HASBRO_BRAND.pageBg }}>
                Checking access…
            </div>
        );
    }
    if (!user) return <Navigate to="/login?brand=hasbro" replace />;

    if (!isHasbroEmail(user.email) || clientId !== HASBRO_COMPANY_ID) {
        return (
            <Screen icon={ShieldAlert} title="Access restricted">
                The Hasbro workspace is only available to {HASBRO_EMAIL_DOMAIN} accounts assigned to Hasbro.
                Contact your TinyGlobal administrator if you need access.
            </Screen>
        );
    }

    if (!(verified ?? user.emailVerified)) {
        const run = fn => async () => {
            setBusy(true);
            setStatus("");
            try { await fn(); }
            catch (err) {
                console.error("Email verification failed:", err);
                setStatus(err.response?.data?.error || "Something went wrong. Please try again in a few minutes.");
            } finally { setBusy(false); }
        };
        // Code sent by the backend through Resend (corporate filters block Firebase's mailer and
        // link scanners use up one-time links, so it's a code rather than a link)
        const sendCode = run(async () => {
            const res = await api.post("/api/hasbro/send-verification");
            if (res.data?.alreadyVerified) return finish();
            setCodeSent(true);
            setStatus(`Code sent to ${user.email}. Check your inbox and junk folder — it expires in 15 minutes.`);
        });
        const confirmCode = run(async () => {
            await api.post("/api/hasbro/confirm-verification", { code });
            await finish();
        });
        // Refresh the Firebase user and ID token so the app and backend see email_verified = true
        const finish = async () => {
            await auth.currentUser.reload();
            await auth.currentUser.getIdToken(true);
            setVerified(auth.currentUser.emailVerified);
        };
        return (
            <Screen icon={MailCheck} title="Verify your email">
                <p>Confirm that you own {user.email} before opening the Hasbro workspace. This is a one-time step.</p>
                {codeSent && (
                    <form onSubmit={e => { e.preventDefault(); confirmCode(); }} className="flex gap-2 justify-center mt-5">
                        <input
                            value={code} onChange={e => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                            inputMode="numeric" autoComplete="one-time-code" placeholder="6-digit code" autoFocus
                            className="w-40 px-3 py-2 rounded-lg bg-white/10 border border-white/30 text-white placeholder-white/50 text-center tracking-[0.3em] focus:outline-none focus:border-[#00B7EB]"
                        />
                        <button type="submit" disabled={busy || code.length !== 6}
                            className="px-4 py-2 rounded-lg bg-[#00B7EB] hover:bg-[#0087C9] text-white text-sm font-semibold disabled:opacity-50">
                            Verify
                        </button>
                    </form>
                )}
                <div className="flex justify-center mt-4">
                    <button onClick={sendCode} disabled={busy}
                        className={codeSent
                            ? "text-xs text-white/70 hover:text-white underline disabled:opacity-50 flex items-center gap-1.5"
                            : "px-4 py-2 rounded-lg bg-[#00B7EB] hover:bg-[#0087C9] text-white text-sm font-semibold disabled:opacity-50"}>
                        {busy && !codeSent && <RefreshCw size={13} className="animate-spin inline mr-1.5" />}
                        {codeSent ? "Send a new code" : "Email me a code"}
                    </button>
                </div>
                {status && <p className="mt-4 text-xs text-white/80">{status}</p>}
            </Screen>
        );
    }

    return children;
}

function Screen({ icon: Icon, title, children }) {
    return (
        <div className="min-h-screen flex items-center justify-center p-6" style={{ background: HASBRO_BRAND.pageBg }}>
            <div className="max-w-md w-full bg-[#003B6B]/60 backdrop-blur border border-white/20 rounded-2xl p-8 text-center text-sm text-white/80 shadow-2xl">
                <img src={HASBRO_BRAND.logo} alt="Hasbro" className="w-16 h-16 rounded-2xl mx-auto mb-4 shadow-lg" />
                <Icon size={22} className="mx-auto text-[#00B7EB] mb-2" />
                <h1 className="text-xl font-bold text-white mb-2">{title}</h1>
                <div>{children}</div>
                <button
                    onClick={() => { localStorage.clear(); auth.signOut().finally(() => { window.location.href = "/login"; }); }}
                    className="mt-6 text-xs text-white/60 hover:text-white underline"
                >
                    Sign in with a different account
                </button>
            </div>
        </div>
    );
}
